import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  List as ListIcon, MapPin, Clock, Users, X, Trash2, Loader2, Edit3, CheckCircle2,
} from "lucide-react";
import LiveSearch, { matchesQuery } from "@/components/LiveSearch";
import {
  WO_TIPO_LABEL, WO_ESTADO_LABEL, WO_ESTADO_STYLE, WO_PRIORIDAD_LABEL,
  WO_PRIORIDAD_STYLE, WO_TIPO_DOT, formatFechaCorta, buildMonthGrid, todayLocalYmd,
} from "@/lib/workOrders";

const DEPARTAMENTOS = ["ADMIN", "CLIMA", "CONS", "EST", "ETEL", "FONT/PCI", "FV", "PDS"];


// Compute a human "duración estimada" suggestion from date/time inputs.
// Rules:
//   · dates + hours (same day)  → "Xh" or "Xh Ym"
//   · todo_el_dia + date range  → "N días" (inclusive)
//   · date range, no hours      → "N días"
//   · nothing computable        → ""
function computeDurationHint(fecha, fechaFin, horaInicio, horaFin, todoElDia) {
  if (!fecha) return "";
  const endDate = fechaFin && fechaFin >= fecha ? fechaFin : fecha;
  const dayMs = 24 * 60 * 60 * 1000;
  const dStart = new Date(fecha + "T00:00:00");
  const dEnd = new Date(endDate + "T00:00:00");
  const daySpan = Math.round((dEnd - dStart) / dayMs) + 1;
  const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;

  if (todoElDia) return plural(daySpan, "día", "días");

  if (horaInicio && horaFin) {
    const [h1, m1] = horaInicio.split(":").map(Number);
    const [h2, m2] = horaFin.split(":").map(Number);
    if (Number.isNaN(h1) || Number.isNaN(h2)) return "";
    const mins = (h2 - h1) * 60 + (m2 - m1);
    if (daySpan > 1) return plural(daySpan, "día", "días");
    if (mins <= 0) return "";
    const H = Math.floor(mins / 60);
    const M = mins % 60;
    if (M === 0) return `${H}h`;
    if (H === 0) return `${M}min`;
    return `${H}h ${M}min`;
  }
  if (daySpan > 1) return plural(daySpan, "día", "días");
  return "";
}

// Expand a range order to the list of dates it covers, inclusive.
function _expandOrderDates(order) {
  if (!order.fecha_fin || order.fecha_fin === order.fecha) return [order.fecha];
  const dates = [];
  const start = new Date(order.fecha + "T00:00:00");
  const end = new Date(order.fecha_fin + "T00:00:00");
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [order.fecha];
  // Use LOCAL date components (not toISOString which converts to UTC and can
  // roll the date back by one during Canary summer time / DST).
  const localYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  for (let i = 0, d = new Date(start); d <= end && i < 366; i++, d.setDate(d.getDate() + 1)) {
    // Skip weekends inside multi-day ranges: an order that spans Fri→Tue must
    // NOT appear on Sat/Sun. Single-day orders (start === end) already
    // returned above without hitting this loop, so a real Saturday order
    // still shows correctly.
    const dow = d.getDay(); // 0=Sun, 6=Sat
    if (dow === 0 || dow === 6) continue;
    dates.push(localYmd(d));
  }
  return dates;
}

// ─────────────────────────────────────────────────────────────────────────
// Main page — /orders
// ─────────────────────────────────────────────────────────────────────────
export default function Orders() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "sadmin";

  const [view, setView] = useState("calendar"); // "calendar" | "list" | "history"
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // an order object
  const [creating, setCreating] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [query, setQuery] = useState("");

  // Filter used when fetching. Employees always see only theirs (backend enforces).
  const monthStart = useMemo(() => {
    const d = new Date(cursor.year, cursor.month, 1);
    return d.toISOString().slice(0, 10);
  }, [cursor]);
  const monthEnd = useMemo(() => {
    // last calendar cell (Sunday of last week) — 42-cell grid
    const cells = buildMonthGrid(cursor.year, cursor.month);
    return cells[cells.length - 1].date;
  }, [cursor]);

  const [deptFilter, setDeptFilter] = useState("");
  // Anchor day for the week view — any date inside the target week.
  const [weekAnchor, setWeekAnchor] = useState(() => todayLocalYmd());

  // ── List view filters (día/hora) — sólo aplican en vista "Lista"
  const [listDateFrom, setListDateFrom] = useState("");
  const [listDateTo, setListDateTo] = useState("");
  const [listTimeFrom, setListTimeFrom] = useState("");
  const [listTimeTo, setListTimeTo] = useState("");
  const clearListFilters = () => {
    setListDateFrom(""); setListDateTo(""); setListTimeFrom(""); setListTimeTo("");
  };
  const listFiltersActive = Boolean(
    listDateFrom || listDateTo || listTimeFrom || listTimeTo
  );

  // Compute the 5 weekdays (Mon-Fri) for the current weekAnchor.
  const weekDays = useMemo(() => {
    const [y, m, d] = weekAnchor.split("-").map(Number);
    const base = new Date(y, m - 1, d);
    const dow = base.getDay() || 7;
    base.setDate(base.getDate() - (dow - 1));
    const out = [];
    for (let i = 0; i < 5; i++) {
      const yy = base.getFullYear();
      const mm = String(base.getMonth() + 1).padStart(2, "0");
      const dd = String(base.getDate()).padStart(2, "0");
      out.push(`${yy}-${mm}-${dd}`);
      base.setDate(base.getDate() + 1);
    }
    return out;
  }, [weekAnchor]);

  const shiftWeek = (delta) => {
    const [y, m, d] = weekAnchor.split("-").map(Number);
    const base = new Date(y, m - 1, d);
    base.setDate(base.getDate() + delta);
    const yy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, "0");
    const dd = String(base.getDate()).padStart(2, "0");
    setWeekAnchor(`${yy}-${mm}-${dd}`);
  };

  const weekLabel = useMemo(() => {
    if (!weekDays.length) return "";
    const first = new Date(weekDays[0] + "T00:00:00");
    const last = new Date(weekDays[weekDays.length - 1] + "T00:00:00");
    const opts = { day: "numeric", month: "short" };
    return `${first.toLocaleDateString("es-ES", opts)} – ${last.toLocaleDateString("es-ES", opts)}`;
  }, [weekDays]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let params;
      if (view === "history") {
        params = {
          fecha_desde: `${new Date().getFullYear() - 5}-01-01`,
          fecha_hasta: `${new Date().getFullYear() + 2}-12-31`,
          limit: 1000,
        };
      } else if (view === "week") {
        // Fetch a slightly wider window (Mon-Sun) so multi-day orders that
        // extend past the visible weekdays still resolve correctly.
        const monISO = weekDays[0];
        const [wy, wm, wd] = weekDays[weekDays.length - 1].split("-").map(Number);
        const sunday = new Date(wy, wm - 1, wd + 2);
        const sunISO = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, "0")}-${String(sunday.getDate()).padStart(2, "0")}`;
        params = { fecha_desde: monISO, fecha_hasta: sunISO, limit: 500 };
      } else if (view === "list" && (listDateFrom || listDateTo)) {
        // Lista con filtro de fecha explícito: consultamos el rango pedido
        // (permite ver más de un mes de golpe).
        params = {
          fecha_desde: listDateFrom || `${new Date().getFullYear() - 5}-01-01`,
          fecha_hasta: listDateTo || `${new Date().getFullYear() + 2}-12-31`,
          limit: 1000,
        };
      } else {
        params = { fecha_desde: monthStart, fecha_hasta: monthEnd, limit: 500 };
      }
      if (deptFilter) params.departamento = deptFilter;
      const { data } = await api.get("/work-orders", { params });
      setOrders(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las órdenes");
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd, view, deptFilter, weekDays, listDateFrom, listDateTo]);
  useEffect(() => { load(); }, [load]);

  // Group orders by date for the calendar grid
  const visibleOrders = useMemo(
    () => orders.filter((o) =>
      matchesQuery(
        query,
        o.titulo,
        o.direccion,
        o.cliente,
        o.descripcion,
        o.departamento,
        (o.asignados || []).map((a) => a.name).join(" "),
      )
    ),
    [orders, query]
  );

  // Extra filters used only by the "Lista" view (día + hora)
  const listFilteredOrders = useMemo(() => {
    if (!listFiltersActive) return visibleOrders;
    const hasTimeFilter = Boolean(listTimeFrom || listTimeTo);
    return visibleOrders.filter((o) => {
      // Date overlap: [o.fecha, o.fecha_fin||o.fecha] ∩ [listDateFrom, listDateTo]
      const oStart = o.fecha || "";
      const oEnd = o.fecha_fin || o.fecha || "";
      if (listDateFrom && oEnd && oEnd < listDateFrom) return false;
      if (listDateTo && oStart && oStart > listDateTo) return false;
      // Hour overlap. Orders todo_el_dia or sin hora → siempre pasan el filtro
      // de hora (para que el usuario no las pierda accidentalmente).
      if (hasTimeFilter && !o.todo_el_dia && o.hora_inicio) {
        const oh1 = o.hora_inicio;
        const oh2 = o.hora_fin || o.hora_inicio;
        if (listTimeFrom && oh2 < listTimeFrom) return false;
        if (listTimeTo && oh1 > listTimeTo) return false;
      }
      return true;
    });
  }, [visibleOrders, listFiltersActive, listDateFrom, listDateTo, listTimeFrom, listTimeTo]);

  // Best-effort "Día X/N" for cards, computed from currently loaded orders.
  // The detail modal fetches the real serie to show accurate counts.
  const serieMap = useMemo(() => {
    const groups = {};
    for (const o of orders) {
      if (!o.serie_id) continue;
      (groups[o.serie_id] = groups[o.serie_id] || []).push(o);
    }
    const out = {};
    for (const k of Object.keys(groups)) {
      const sorted = groups[k].sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
      sorted.forEach((o, idx) => { out[o.id] = { pos: idx + 1, total: sorted.length }; });
    }
    return out;
  }, [orders]);
  const ordersByDate = useMemo(() => {
    const map = {};
    for (const o of visibleOrders) {
      // Expand ranged orders across all days they cover; the calendar cell
      // rendering annotates each occurrence with position (start / middle / end).
      const dates = _expandOrderDates(o);
      dates.forEach((d, idx) => {
        (map[d] = map[d] || []).push({
          ...o,
          _rangePos: dates.length === 1
            ? "single"
            : idx === 0 ? "start" : idx === dates.length - 1 ? "end" : "middle",
          _rangeIndex: idx + 1,
          _rangeTotal: dates.length,
        });
      });
    }
    // sort each day by hora_inicio, "todo_el_dia" and ranged orders first
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const ka = a.todo_el_dia || a._rangeTotal > 1 ? "0" : (a.hora_inicio || "99");
        const kb = b.todo_el_dia || b._rangeTotal > 1 ? "0" : (b.hora_inicio || "99");
        return ka.localeCompare(kb);
      });
    }
    return map;
  }, [visibleOrders]);

  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

  const monthLabel = useMemo(() => {
    const d = new Date(cursor.year, cursor.month, 1);
    return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  }, [cursor]);

  const prevMonth = () => setCursor((c) => {
    const d = new Date(c.year, c.month - 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const nextMonth = () => setCursor((c) => {
    const d = new Date(c.year, c.month + 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const goToday = () => {
    const d = new Date();
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };

  const onSaved = () => {
    setCreating(false);
    setEditingOrder(null);
    load();
  };
  const onDeleted = () => {
    setSelected(null);
    load();
  };

  return (
    <div className="space-y-5" data-testid="orders-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="label-up text-[#F5A623]">Agenda</div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-1">Órdenes de trabajo y Planificación de obra</h1>
          <p className="text-zinc-600 text-sm mt-1">
            {isManager
              ? "Asigna trabajo a tu equipo. Cada empleado verá aquí lo que le toca."
              : "Aquí ves las órdenes de trabajo y calificaciones que te han asignado."}
          </p>
        </div>
        {isManager && (
          <button
            data-testid="orders-create-btn"
            onClick={() => setCreating(true)}
            className="brutal-btn-primary h-11 px-4 text-sm inline-flex items-center gap-2 shrink-0"
          >
            <Plus size={16} /> Nueva orden
          </button>
        )}
      </header>

      <LiveSearch
        value={query}
        onChange={setQuery}
        placeholder="Buscar por título, dirección, cliente, asignado…"
        testId="orders-search"
      />

      {isManager && (
        <div className="flex items-center gap-2">
          <label className="label-up whitespace-nowrap">Departamento</label>
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            data-testid="orders-dept-filter"
            className="brutal-input flex-1 h-9 text-sm"
          >
            <option value="">Todos los departamentos</option>
            {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}

      {/* View toggle + month nav */}
      <div className="brutal-card p-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          <button
            data-testid="view-calendar"
            onClick={() => setView("calendar")}
            className={`h-9 px-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
              view === "calendar" ? "bg-[#0B2A4A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <CalendarIcon size={14} /> Calendario
          </button>
          <button
            data-testid="view-week"
            onClick={() => setView("week")}
            className={`h-9 px-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
              view === "week" ? "bg-[#0B2A4A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <CalendarIcon size={14} /> Semana
          </button>
          <button
            data-testid="view-list"
            onClick={() => setView("list")}
            className={`h-9 px-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
              view === "list" ? "bg-[#0B2A4A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <ListIcon size={14} /> Lista
          </button>
          <button
            data-testid="view-history"
            onClick={() => setView("history")}
            className={`h-9 px-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
              view === "history" ? "bg-[#0B2A4A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <ListIcon size={14} /> Historial
          </button>
        </div>
        <div className="inline-flex items-center gap-1">
          {view === "week" ? (
            <>
              <button
                onClick={() => shiftWeek(-7)}
                data-testid="orders-prev-week"
                className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setWeekAnchor(todayLocalYmd())}
                data-testid="orders-this-week"
                className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider hover:bg-slate-50 min-w-[140px]"
              >
                {weekLabel}
              </button>
              <button
                onClick={() => shiftWeek(7)}
                data-testid="orders-next-week"
                className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center"
              >
                <ChevronRight size={16} />
              </button>
            </>
          ) : view !== "history" && !(view === "list" && (listDateFrom || listDateTo)) ? (
            <>
              <button
                onClick={prevMonth}
                data-testid="orders-prev-month"
                className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={goToday}
                data-testid="orders-today"
                className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider hover:bg-slate-50 min-w-[140px] capitalize"
              >
                {monthLabel}
              </button>
              <button
                onClick={nextMonth}
                data-testid="orders-next-month"
                className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center"
              >
                <ChevronRight size={16} />
              </button>
            </>
          ) : null}
        </div>
      </div>

      {loading && (
        <div className="brutal-card p-6 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      )}

      {view === "list" && (
        <ListFiltersPanel
          dateFrom={listDateFrom}
          dateTo={listDateTo}
          timeFrom={listTimeFrom}
          timeTo={listTimeTo}
          onDateFromChange={setListDateFrom}
          onDateToChange={setListDateTo}
          onTimeFromChange={setListTimeFrom}
          onTimeToChange={setListTimeTo}
          onClear={clearListFilters}
          active={listFiltersActive}
          resultCount={listFilteredOrders.length}
        />
      )}

      {view === "calendar" ? (
        <CalendarGrid cells={cells} ordersByDate={ordersByDate} onClickOrder={setSelected} />
      ) : view === "week" ? (
        <WeekView days={weekDays} ordersByDate={ordersByDate} onClickOrder={setSelected} serieMap={serieMap} />
      ) : view === "history" ? (
        <OrdersHistoryView orders={visibleOrders} onClickOrder={setSelected} serieMap={serieMap} />
      ) : (
        <OrdersListView orders={listFilteredOrders} onClickOrder={setSelected} serieMap={serieMap} />
      )}

      {/* View / edit modal */}
      {selected && (
        <OrderDetailModal
          order={selected}
          canManage={isManager}
          currentUserId={user?.id}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditingOrder(selected); setSelected(null); }}
          onDeleted={onDeleted}
        />
      )}

      {/* Create / edit form modal */}
      {(creating || editingOrder) && (
        <OrderFormModal
          order={editingOrder}
          onClose={() => { setCreating(false); setEditingOrder(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Calendar grid (month view) — Google-style
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// Week view — Mon–Fri, tasks shown inline so the manager sees obra + workers
// at a glance without opening each card. Optimised for a mobile screen.
// ─────────────────────────────────────────────────────────────────────────
const _DOW_LABELS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];

function WeekView({ days, ordersByDate, onClickOrder, serieMap = {} }) {
  const today = todayLocalYmd();
  return (
    <div className="space-y-2" data-testid="orders-week-view">
      {days.map((iso, i) => {
        const list = ordersByDate[iso] || [];
        const isToday = iso === today;
        const [y, m, d] = iso.split("-").map(Number);
        const dayNum = d;
        const monthShort = new Date(y, m - 1, d).toLocaleDateString("es-ES", { month: "short" });
        return (
          <section
            key={iso}
            data-testid={`week-day-${iso}`}
            className={`brutal-card p-3 ${isToday ? "ring-2 ring-[#F5A623]" : ""}`}
          >
            <header className="flex items-baseline justify-between mb-2">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-display font-black leading-none text-[#0B2A4A]">
                  {dayNum}
                </span>
                <span className="label-up text-[10px] capitalize">
                  {_DOW_LABELS[i]} · {monthShort}
                </span>
                {isToday && (
                  <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-[#F5A623] text-[#0B2A4A]">
                    Hoy
                  </span>
                )}
              </div>
              <span className="text-[10px] text-slate-500">
                {list.length} tarea{list.length === 1 ? "" : "s"}
              </span>
            </header>

            {list.length === 0 ? (
              <p className="text-xs text-slate-400 italic pl-1">Sin tareas</p>
            ) : (
              <ul className="space-y-1.5">
                {list.map((o) => (
                  <li key={`${iso}-${o.id}`}>
                    <button
                      type="button"
                      onClick={() => onClickOrder(o)}
                      data-testid={`week-row-${o.id}`}
                      className="w-full flex items-start gap-2 text-left rounded-lg border border-slate-200 p-2 hover:bg-slate-50 transition"
                    >
                      <span
                        className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                        style={{ background: WO_TIPO_DOT[o.tipo] || "#94a3b8" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display font-black text-sm text-[#0B2A4A] truncate">
                            {o.titulo}
                          </span>
                          <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${WO_ESTADO_STYLE[o.estado]}`}>
                            {WO_ESTADO_LABEL[o.estado]}
                          </span>
                          {o.prioridad && o.prioridad !== "media" && (
                            <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${WO_PRIORIDAD_STYLE[o.prioridad] || ""}`}>
                              {WO_PRIORIDAD_LABEL[o.prioridad]}
                            </span>
                          )}
                          {o.departamento && (
                            <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                              {o.departamento}
                            </span>
                          )}
                          {o.serie_id && serieMap[o.id] && (
                            <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-[#F5A623]/15 text-[#8a5a00] border border-[#F5A623]/40">
                              {serieMap[o.id].pos}/{serieMap[o.id].total}
                            </span>
                          )}
                        </div>
                        {/* Hora / duración */}
                        {(o.hora_inicio || o.todo_el_dia || o.duracion_estimada) && (
                          <div className="text-[11px] text-slate-600 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                            <Clock size={11} className="text-slate-400" />
                            {o.todo_el_dia
                              ? <span>Día completo</span>
                              : o.hora_inicio && (
                                  <span>{o.hora_inicio}{o.hora_fin ? `–${o.hora_fin}` : ""}</span>
                                )}
                            {o.duracion_estimada && (
                              <span className="text-slate-400">· {o.duracion_estimada}</span>
                            )}
                          </div>
                        )}
                        {/* Cliente */}
                        {o.cliente && (
                          <div className="text-[11px] text-slate-600 mt-0.5 truncate">
                            <span className="text-slate-400">Cliente:</span> <span className="font-semibold">{o.cliente}</span>
                          </div>
                        )}
                        {/* Dirección clicable a Google Maps */}
                        {o.direccion && (
                          <div className="text-[11px] mt-0.5 truncate inline-flex items-start gap-1">
                            <MapPin size={11} className="text-slate-400 mt-0.5 shrink-0" />
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.direccion)}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[#0E78C2] font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid break-words"
                            >
                              {o.direccion}
                            </a>
                          </div>
                        )}
                        {/* Descripción / notas (2 líneas máx) */}
                        {(o.descripcion || o.notas) && (
                          <p
                            className="text-[11px] text-slate-500 mt-0.5 overflow-hidden"
                            style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                          >
                            {o.descripcion || o.notas}
                          </p>
                        )}
                        {/* Asignados */}
                        {Array.isArray(o.asignados) && o.asignados.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {o.asignados.map((a) => (
                              <span
                                key={a.user_id}
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#0B2A4A]/5 text-[#0B2A4A] border border-[#0B2A4A]/10"
                              >
                                {a.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}



function CalendarGrid({ cells, ordersByDate, onClickOrder }) {
  const weekdays = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
  return (
    <div className="brutal-card p-0 overflow-hidden" data-testid="orders-calendar">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
        {weekdays.map((d) => (
          <div key={d} className="text-[10px] uppercase font-bold tracking-wider text-slate-500 text-center py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-[minmax(84px,auto)]">
        {cells.map((c) => {
          const dayOrders = ordersByDate[c.date] || [];
          return (
            <div
              key={c.date}
              className={`relative border-b border-r border-slate-100 p-1.5 flex flex-col gap-1 min-h-[84px] ${
                c.inMonth ? "bg-white" : "bg-slate-50/50"
              }`}
              data-testid={`orders-cell-${c.date}`}
            >
              <div
                className={`text-[10px] font-semibold self-start ${
                  c.isToday
                    ? "bg-[#F5A623] text-white rounded-full w-5 h-5 inline-flex items-center justify-center"
                    : c.inMonth ? "text-slate-700" : "text-slate-300"
                }`}
              >
                {c.dayOfMonth}
              </div>
              <div className="flex flex-col gap-0.5 overflow-hidden">
                {dayOrders.slice(0, 3).map((o) => {
                  const isAllDay = o.todo_el_dia;
                  const isRange = o._rangeTotal > 1;
                  const pos = o._rangePos;
                  // Visual prefix: hora only for single-day timed orders; arrows for ranges; ◐ for all-day
                  const prefix = isRange
                    ? (pos === "start" ? "→ " : pos === "end" ? "← " : "· ")
                    : isAllDay ? "◐ " : (o.hora_inicio ? o.hora_inicio + " " : "");
                  return (
                    <button
                      key={o.id + "-" + (o._rangeIndex || 0)}
                      type="button"
                      onClick={() => onClickOrder(o)}
                      data-testid={`orders-event-${o.id}`}
                      title={
                        isRange
                          ? `${o.titulo} (${o._rangeIndex}/${o._rangeTotal})`
                          : `${o.titulo}${o.hora_inicio ? " · " + o.hora_inicio : ""}`
                      }
                      className={`text-left text-[10px] leading-tight rounded px-1 py-0.5 truncate inline-flex items-center gap-1 border ${WO_ESTADO_STYLE[o.estado] || WO_ESTADO_STYLE.pendiente} hover:opacity-90 ${
                        isRange ? "border-l-2 border-l-[#F5A623]" : ""
                      }`}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: WO_TIPO_DOT[o.tipo] || "#94a3b8" }}
                      />
                      <span className="truncate font-semibold">{prefix}{o.titulo}</span>
                    </button>
                  );
                })}
                {dayOrders.length > 3 && (
                  <button
                    onClick={() => onClickOrder(dayOrders[0])}
                    className="text-[10px] text-slate-500 hover:text-slate-800 font-semibold pl-1"
                  >
                    +{dayOrders.length - 3} más
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// List view
// ─────────────────────────────────────────────────────────────────────────
function ListFiltersPanel({
  dateFrom, dateTo, timeFrom, timeTo,
  onDateFromChange, onDateToChange, onTimeFromChange, onTimeToChange,
  onClear, active, resultCount,
}) {
  return (
    <div className="brutal-card p-3 space-y-3" data-testid="orders-list-filters">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="label-up text-slate-500">Filtrar por día y hora</span>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span data-testid="orders-list-filters-count">
            {resultCount} resultado{resultCount === 1 ? "" : "s"}
          </span>
          {active && (
            <button
              type="button"
              onClick={onClear}
              data-testid="orders-list-filters-clear"
              className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Desde</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            data-testid="orders-list-filter-date-from"
            className="brutal-input h-9 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Hasta</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            data-testid="orders-list-filter-date-to"
            className="brutal-input h-9 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Hora desde</label>
          <input
            type="time"
            value={timeFrom}
            onChange={(e) => onTimeFromChange(e.target.value)}
            data-testid="orders-list-filter-time-from"
            className="brutal-input h-9 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Hora hasta</label>
          <input
            type="time"
            value={timeTo}
            onChange={(e) => onTimeToChange(e.target.value)}
            data-testid="orders-list-filter-time-to"
            className="brutal-input h-9 text-sm"
          />
        </div>
      </div>
      <p className="text-[11px] text-slate-500">
        Consejo: usa la misma fecha en <b>Desde</b> y <b>Hasta</b> para ver un día concreto.
        Las órdenes marcadas como "todo el día" aparecen aunque uses filtro de hora.
      </p>
    </div>
  );
}


function OrdersListView({ orders, onClickOrder, serieMap = {} }) {
  if (orders.length === 0) {
    return (
      <div className="brutal-card p-6 text-center text-slate-500" data-testid="orders-list-empty">
        No hay órdenes que coincidan con los filtros.
      </div>
    );
  }
  const sorted = [...orders].sort((a, b) => {
    const df = (a.fecha || "").localeCompare(b.fecha || "");
    if (df !== 0) return df;
    return (a.hora_inicio || "99").localeCompare(b.hora_inicio || "99");
  });
  return (
    <ul className="space-y-2" data-testid="orders-list">
      {sorted.map((o) => {
        const s = o.serie_id ? serieMap[o.id] : null;
        return (
        <li key={o.id}>
          <button
            type="button"
            onClick={() => onClickOrder(o)}
            data-testid={`orders-row-${o.id}`}
            className="w-full brutal-card p-4 flex items-start gap-3 text-left transition-all hover:-translate-y-[1px] hover:shadow-lg"
          >
            <span
              className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0"
              style={{ background: WO_TIPO_DOT[o.tipo] || "#94a3b8" }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${WO_ESTADO_STYLE[o.estado]}`}>
                  {WO_ESTADO_LABEL[o.estado]}
                </span>
                <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${WO_PRIORIDAD_STYLE[o.prioridad] || ""}`}>
                  {WO_PRIORIDAD_LABEL[o.prioridad]}
                </span>
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                  {WO_TIPO_LABEL[o.tipo]}
                </span>
                {s && (
                  <span
                    data-testid={`orders-row-serie-${o.id}`}
                    className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#F5A623]/15 text-[#8a5a00] border border-[#F5A623]/40"
                    title="Parte de una serie multi-día"
                  >
                    Día {s.pos}/{s.total}
                  </span>
                )}
              </div>
              <div className="font-display font-black text-lg text-[#0B2A4A] leading-tight mt-1 truncate">{o.titulo}</div>
              <div className="text-xs text-slate-600 flex items-center gap-3 mt-1 flex-wrap">
                <span className="inline-flex items-center gap-1"><Clock size={12} /> {formatFechaCorta(o.fecha)}{o.hora_inicio ? ` · ${o.hora_inicio}${o.hora_fin ? "–" + o.hora_fin : ""}` : ""}</span>
                {o.direccion && <span className="inline-flex items-center gap-1 truncate"><MapPin size={12} /> {o.direccion}</span>}
                <span className="inline-flex items-center gap-1"><Users size={12} /> {o.asignados?.length || 0}</span>
              </div>
            </div>
          </button>
        </li>
        );
      })}
    </ul>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// History view — flat log grouped by month, latest first, no date filter.
// ─────────────────────────────────────────────────────────────────────────
function OrdersHistoryView({ orders, onClickOrder, serieMap = {} }) {
  if (orders.length === 0) {
    return <div className="brutal-card p-6 text-center text-slate-500">Sin historial todavía.</div>;
  }
  // Sort by fecha desc, then by hora_inicio desc (stable)
  const sorted = [...orders].sort((a, b) => {
    const df = (b.fecha || "").localeCompare(a.fecha || "");
    if (df !== 0) return df;
    return (b.hora_inicio || "").localeCompare(a.hora_inicio || "");
  });
  // Group by YYYY-MM
  const groups = {};
  for (const o of sorted) {
    const k = (o.fecha || "").slice(0, 7) || "sin-fecha";
    (groups[k] = groups[k] || []).push(o);
  }
  const keys = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  return (
    <div className="space-y-5" data-testid="orders-history">
      <div className="brutal-card p-3 flex items-center justify-between text-xs text-slate-500">
        <span>Historial completo · {orders.length} orden{orders.length === 1 ? "" : "es"}</span>
        <span className="text-[10px] uppercase tracking-wider">últimos primero</span>
      </div>
      {keys.map((k) => {
        const [year, month] = k.split("-");
        const label = k === "sin-fecha"
          ? "Sin fecha"
          : new Date(Number(year), Number(month) - 1, 1)
              .toLocaleDateString("es-ES", { month: "long", year: "numeric" });
        return (
          <section key={k}>
            <h3 className="label-up text-[#F5A623] mb-2 capitalize">{label} · {groups[k].length}</h3>
            <ul className="space-y-2">
              {groups[k].map((o) => {
                const s = o.serie_id ? serieMap[o.id] : null;
                return (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => onClickOrder(o)}
                    data-testid={`history-row-${o.id}`}
                    className="w-full brutal-card p-3 flex items-start gap-3 text-left transition-all hover:-translate-y-[1px] hover:shadow-lg"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0"
                      style={{ background: WO_TIPO_DOT[o.tipo] || "#94a3b8" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${WO_ESTADO_STYLE[o.estado]}`}>
                          {WO_ESTADO_LABEL[o.estado]}
                        </span>
                        <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                          {WO_TIPO_LABEL[o.tipo]}
                        </span>
                        {o.departamento && (
                          <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                            {o.departamento}
                          </span>
                        )}
                        {s && (
                          <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#F5A623]/15 text-[#8a5a00] border border-[#F5A623]/40">
                            Día {s.pos}/{s.total}
                          </span>
                        )}
                      </div>
                      <div className="font-display font-black text-base text-[#0B2A4A] leading-tight mt-1 truncate">{o.titulo}</div>
                      <div className="text-[11px] text-slate-600 flex items-center gap-3 mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Clock size={12} />
                          {formatFechaCorta(o.fecha)}
                          {o.fecha_fin && o.fecha_fin !== o.fecha ? ` → ${formatFechaCorta(o.fecha_fin)}` : ""}
                        </span>
                        {o.direccion && <span className="inline-flex items-center gap-1 truncate"><MapPin size={12} /> {o.direccion}</span>}
                        <span className="inline-flex items-center gap-1"><Users size={12} /> {o.asignados?.length || 0}</span>
                      </div>
                    </div>
                  </button>
                </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Detail modal (view + status change + delete)
// ─────────────────────────────────────────────────────────────────────────
function OrderDetailModal({ order, canManage, currentUserId, onClose, onEdit, onDeleted }) {
  const isAssignee = (order.asignados || []).some((a) => a.user_id === currentUserId);
  const [busy, setBusy] = useState(false);
  const [depByUserId, setDepByUserId] = useState({});
  // Serie context (fetched only if the order belongs to a serie)
  const [serieList, setSerieList] = useState(null); // null=unloaded, []=none
  const [askScope, setAskScope] = useState(null);   // "delete" | null

  // Enrich the assignee list with departamento by cross-referencing the users
  // directory. Cached in-memory so we do not hit the API repeatedly.
  useEffect(() => {
    let alive = true;
    api.get("/users/directory")
      .then(({ data }) => {
        if (!alive) return;
        const map = {};
        (data || []).forEach((u) => { map[u.id] = u.departamento || ""; });
        setDepByUserId(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Fetch serie members so we can render "Día X de N" and power the scope dialog.
  useEffect(() => {
    if (!order.serie_id || !canManage) return;
    let alive = true;
    api.get(`/work-orders/serie/${order.serie_id}`)
      .then(({ data }) => { if (alive) setSerieList(data || []); })
      .catch(() => { if (alive) setSerieList([]); });
    return () => { alive = false; };
  }, [order.serie_id, canManage]);

  const serieIndex = useMemo(() => {
    if (!serieList || !serieList.length) return null;
    const sorted = [...serieList].sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
    const idx = sorted.findIndex((o) => o.id === order.id);
    return idx >= 0 ? { pos: idx + 1, total: sorted.length, members: sorted } : null;
  }, [serieList, order.id]);

  const doDelete = async (scope, rangeFrom, rangeTo) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ scope });
      if (scope === "range") {
        params.set("range_from", rangeFrom);
        params.set("range_to", rangeTo);
      }
      const res = await api.delete(`/work-orders/${order.id}?${params.toString()}`);
      toast.success(`${res.data?.deleted ?? 1} orden(es) eliminada(s)`);
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally { setBusy(false); setAskScope(null); }
  };

  const remove = async () => {
    if (order.serie_id && serieIndex) {
      setAskScope("delete");
      return;
    }
    if (!window.confirm("¿Eliminar esta orden?")) return;
    await doDelete("only");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="order-detail-modal"
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ background: WO_TIPO_DOT[order.tipo] || "#94a3b8" }}
            />
            <div className="label-up">{WO_TIPO_LABEL[order.tipo]}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-rose-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <h3 className="font-display font-black text-2xl text-[#0B2A4A] leading-tight">{order.titulo}</h3>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${WO_ESTADO_STYLE[order.estado]}`}>
                {WO_ESTADO_LABEL[order.estado]}
              </span>
              <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${WO_PRIORIDAD_STYLE[order.prioridad] || ""}`}>
                Prio {WO_PRIORIDAD_LABEL[order.prioridad]}
              </span>
              {order.departamento && (
                <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                  {order.departamento}
                </span>
              )}
              {order.serie_id && serieIndex && (
                <span
                  data-testid="order-serie-badge"
                  className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#F5A623]/15 text-[#8a5a00] border border-[#F5A623]/40"
                  title="Esta orden forma parte de una serie multi-día"
                >
                  Día {serieIndex.pos}/{serieIndex.total} · Serie
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoRow
              icon={<Clock size={14} />}
              label={order.fecha_fin && order.fecha_fin !== order.fecha ? "Del" : "Fecha"}
              value={
                order.fecha_fin && order.fecha_fin !== order.fecha
                  ? `${formatFechaCorta(order.fecha)} al ${formatFechaCorta(order.fecha_fin)}`
                  : formatFechaCorta(order.fecha)
              }
            />
            <InfoRow
              icon={<Clock size={14} />}
              label="Horario"
              value={
                order.todo_el_dia
                  ? "Día completo"
                  : order.hora_inicio
                    ? `${order.hora_inicio}${order.hora_fin ? "–" + order.hora_fin : ""}`
                    : "—"
              }
            />
            {order.duracion_estimada && <InfoRow label="Duración estimada" value={order.duracion_estimada} />}
            {order.cliente && <InfoRow label="Cliente" value={order.cliente} />}
          </div>

          {order.direccion && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-slate-400 mt-0.5 shrink-0"><MapPin size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="label-up text-[9px]">Dirección</div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.direccion)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="order-direccion-link"
                  className="text-[#0E78C2] font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid break-words"
                >
                  {order.direccion}
                </a>
              </div>
            </div>
          )}

          {order.descripcion && (
            <div>
              <div className="label-up mb-1">Descripción</div>
              <p className="text-sm whitespace-pre-wrap text-slate-700">{order.descripcion}</p>
            </div>
          )}

          <div>
            <div className="label-up mb-1">Asignados ({order.asignados?.length || 0})</div>
            {order.asignados?.length ? (
              <ul className="text-sm space-y-1">
                {order.asignados.map((a) => (
                  <li key={a.user_id} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623]" />
                    <span>{a.name}</span>
                    {depByUserId[a.user_id] && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {depByUserId[a.user_id]}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : <p className="text-xs text-slate-500">Sin asignar</p>}
          </div>

          {order.notas && (
            <div>
              <div className="label-up mb-1">Notas</div>
              <p className="text-sm whitespace-pre-wrap text-slate-700">{order.notas}</p>
            </div>
          )}

          {order.created_by_name && (
            <div className="pt-2 border-t border-slate-100">
              <div className="label-up mb-1">Creado por</div>
              <p className="text-sm text-slate-700 font-semibold" data-testid="order-created-by">
                {order.created_by_name}
                {order.created_at && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    · {formatFechaCorta(order.created_at.slice(0, 10))}
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 space-y-2">
          {canManage && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onEdit}
                disabled={busy}
                data-testid="order-edit"
                className="flex-1 h-9 rounded-lg border border-slate-200 text-xs font-bold uppercase tracking-wider hover:bg-slate-50 inline-flex items-center justify-center gap-1.5"
              >
                <Edit3 size={12} /> Editar
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                data-testid="order-delete"
                className="flex-1 h-9 rounded-lg border border-rose-300 text-rose-700 text-xs font-bold uppercase tracking-wider hover:bg-rose-50 inline-flex items-center justify-center gap-1.5"
              >
                <Trash2 size={12} /> Eliminar
              </button>
            </div>
          )}
          {!canManage && isAssignee && (
            <div className="text-[11px] text-slate-500 text-center">
              Solo puedes consultar esta orden. Contacta con un administrador para cambios.
            </div>
          )}
        </div>
      </div>

      {askScope === "delete" && serieIndex && (
        <SerieScopeDialog
          title="Eliminar orden"
          intro="Esta orden forma parte de una serie multi-día. ¿Qué días quieres eliminar?"
          currentDate={order.fecha}
          members={serieIndex.members}
          busy={busy}
          confirmLabel="Eliminar"
          confirmClass="border-rose-300 text-rose-700 hover:bg-rose-50"
          onConfirm={doDelete}
          onClose={() => setAskScope(null)}
        />
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {icon && <span className="text-slate-400 mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">
        <div className="label-up text-[9px]">{label}</div>
        <div className="text-slate-800 font-semibold">{value || "—"}</div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Create / edit modal (manager only)
// ─────────────────────────────────────────────────────────────────────────
function OrderFormModal({ order, onClose, onSaved }) {
  const isEdit = !!order;
  const [form, setForm] = useState(() => order || {
    tipo: "orden_trabajo",
    titulo: "",
    direccion: "",
    descripcion: "",
    fecha: todayLocalYmd(),
    fecha_fin: "",
    todo_el_dia: false,
    hora_inicio: "",
    hora_fin: "",
    duracion_estimada: "",
    cliente: "",
    prioridad: "media",
    estado: "pendiente",
    departamento: "",
    asignados: [],
    notas: "",
  });
  const [saving, setSaving] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [assigneeQuery, setAssigneeQuery] = useState("");
  // Serie-scope dialog (only relevant when editing an order that has serie_id)
  const [pendingPayload, setPendingPayload] = useState(null);
  const [serieMembers, setSerieMembers] = useState([]);
  // Track whether the user has manually overridden the auto value in this
  // editing session — after a manual edit we stop overwriting on date changes.
  const manualDurationRef = useRef(false);

  const autoDuration = useMemo(
    () => computeDurationHint(form.fecha, form.fecha_fin, form.hora_inicio, form.hora_fin, form.todo_el_dia),
    [form.fecha, form.fecha_fin, form.hora_inicio, form.hora_fin, form.todo_el_dia],
  );

  useEffect(() => {
    if (manualDurationRef.current) return;
    if (!autoDuration) return;
    setForm((f) => ({ ...f, duracion_estimada: autoDuration }));
  }, [autoDuration]);

  useEffect(() => {
    api.get("/users/directory").then((r) => setDirectory(r.data || [])).catch(() => {});
  }, []);

  // Pre-fetch the serie members (used by the scope dialog range picker) so the
  // dialog opens instantly when the user hits "Guardar".
  useEffect(() => {
    if (!order?.serie_id) return;
    let alive = true;
    api.get(`/work-orders/serie/${order.serie_id}`)
      .then(({ data }) => { if (alive) setSerieMembers(data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [order?.serie_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAssignee = (u) => {
    setForm((f) => {
      const has = f.asignados.some((a) => a.user_id === u.id);
      const next = has
        ? f.asignados.filter((a) => a.user_id !== u.id)
        : [...f.asignados, { user_id: u.id, name: u.name || u.email }];
      return { ...f, asignados: next };
    });
  };

  const persist = async (payload, scope, rangeFrom, rangeTo) => {
    setSaving(true);
    try {
      if (isEdit) {
        const params = new URLSearchParams({ scope: scope || "only" });
        if (scope === "range") {
          params.set("range_from", rangeFrom);
          params.set("range_to", rangeTo);
        }
        await api.put(`/work-orders/${order.id}?${params.toString()}`, payload);
        toast.success(
          scope === "series" ? "Serie actualizada"
          : scope === "range" ? "Rango actualizado"
          : "Orden actualizada"
        );
      } else {
        await api.post("/work-orders", payload);
        toast.success("Orden creada");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
      setPendingPayload(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    if (!form.fecha) { toast.error("La fecha es obligatoria"); return; }
    if (form.fecha_fin && form.fecha_fin < form.fecha) {
      toast.error("La fecha fin no puede ser anterior a la fecha inicio");
      return;
    }
    const payload = {
      ...form,
      // send only ids — server hydrates names
      asignados: form.asignados.map((a) => ({ user_id: a.user_id })),
      // strip empty optional values; if todo_el_dia is on, blank the times
      hora_inicio: form.todo_el_dia ? null : (form.hora_inicio || null),
      hora_fin: form.todo_el_dia ? null : (form.hora_fin || null),
      duracion_estimada: form.duracion_estimada || null,
      departamento: form.departamento || null,
      fecha_fin: form.fecha_fin || null,
    };
    // If we are editing an order that belongs to a serie, ask the manager
    // whether this change applies to just today, a range of days or the whole
    // serie (title / cliente / assignees / etc).
    if (isEdit && order?.serie_id && serieMembers.length > 1) {
      setPendingPayload(payload);
      return;
    }
    await persist(payload, "only");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="order-form-modal"
    >
      <form
        onSubmit={submit}
        className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <h3 className="font-display font-black text-lg text-[#0B2A4A]">
            {isEdit ? "Editar orden" : "Nueva orden"}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-rose-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* Tipo */}
          <div>
            <span className="label-up block mb-1">Tipo</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["orden_trabajo", "Orden de trabajo"],
                ["calificacion", "Planificación de obra"],
              ].map(([k, l]) => (
                <button
                  type="button"
                  key={k}
                  data-testid={`order-tipo-${k}`}
                  onClick={() => update("tipo", k)}
                  className={`h-10 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${
                    form.tipo === k
                      ? "bg-[#0B2A4A] text-white border-[#0B2A4A]"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >{l}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-up block mb-1">Título *</label>
            <input
              className="brutal-input w-full" required data-testid="order-titulo"
              value={form.titulo} onChange={(e) => update("titulo", e.target.value)}
              placeholder="Ej: Montaje FV Hotel Los Verodes"
            />
          </div>

          <div>
            <label className="label-up block mb-1">Dirección</label>
            <input
              className="brutal-input w-full" data-testid="order-direccion"
              value={form.direccion} onChange={(e) => update("direccion", e.target.value)}
              placeholder="C/ ejemplo 12, Arrecife"
            />
          </div>

          <div>
            <label className="label-up block mb-1">Descripción / trabajo</label>
            <textarea
              className="brutal-input w-full min-h-[70px]" data-testid="order-descripcion"
              value={form.descripcion} onChange={(e) => update("descripcion", e.target.value)}
              placeholder="Qué hay que hacer, materiales, etc."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-up block mb-1">Fecha inicio *</label>
              <input type="date" className="brutal-input w-full" required
                data-testid="order-fecha"
                value={form.fecha} onChange={(e) => update("fecha", e.target.value)} />
            </div>
            <div>
              <label className="label-up block mb-1">Fecha fin</label>
              <input type="date" className="brutal-input w-full"
                data-testid="order-fecha-fin"
                min={form.fecha || undefined}
                value={form.fecha_fin || ""}
                onChange={(e) => update("fecha_fin", e.target.value)}
                placeholder="Opcional" />
            </div>
          </div>
          {form.fecha_fin && form.fecha_fin !== form.fecha && (
            <p className="text-[11px] text-slate-500 -mt-1 pl-1">
              Orden multi-día: se pintará en cada día del <b>{form.fecha}</b> al <b>{form.fecha_fin}</b>.
            </p>
          )}

          <label
            className="flex items-center gap-2 select-none cursor-pointer"
            data-testid="order-todo-el-dia-wrapper"
          >
            <input
              type="checkbox"
              data-testid="order-todo-el-dia"
              className="w-5 h-5 brutal-border accent-[#F5A623]"
              checked={!!form.todo_el_dia}
              onChange={(e) => update("todo_el_dia", e.target.checked)}
            />
            <span className="text-sm font-bold uppercase tracking-wider text-slate-700">
              Día completo (sin horario específico)
            </span>
          </label>

          {!form.todo_el_dia && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-up block mb-1">Hora inicio</label>
                <input type="time" className="brutal-input w-full" data-testid="order-hora-inicio"
                  value={form.hora_inicio || ""} onChange={(e) => update("hora_inicio", e.target.value)} />
              </div>
              <div>
                <label className="label-up block mb-1">Hora fin</label>
                <input type="time" className="brutal-input w-full" data-testid="order-hora-fin"
                  value={form.hora_fin || ""} onChange={(e) => update("hora_fin", e.target.value)} />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label-up">Duración estimada</label>
              {autoDuration && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                  Auto · {autoDuration}
                </span>
              )}
            </div>
            <input className="brutal-input w-full" data-testid="order-duracion"
              value={form.duracion_estimada || ""}
              onChange={(e) => { manualDurationRef.current = true; update("duracion_estimada", e.target.value); }}
              placeholder="2h, medio día, 3 días…" />
            <p className="text-[11px] text-slate-500 mt-1">
              Se calcula sola con las fechas y horas. Puedes editarla si quieres.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-up block mb-1">Cliente</label>
              <input className="brutal-input w-full" data-testid="order-cliente"
                value={form.cliente} onChange={(e) => update("cliente", e.target.value)} />
            </div>
            <div>
              <label className="label-up block mb-1">Departamento</label>
              <select className="brutal-input w-full" data-testid="order-departamento"
                value={form.departamento || ""} onChange={(e) => update("departamento", e.target.value)}>
                <option value="">—</option>
                {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="label-up block mb-1">Prioridad</label>
            <select className="brutal-input w-full" data-testid="order-prioridad"
              value={form.prioridad} onChange={(e) => update("prioridad", e.target.value)}>
              {Object.entries(WO_PRIORIDAD_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>

          {/* Assignees picker */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="label-up">Asignar a ({form.asignados.length})</span>
              {form.asignados.length > 0 && (
                <button type="button"
                  onClick={() => update("asignados", [])}
                  className="text-[10px] text-rose-600 font-semibold uppercase tracking-wider">
                  Limpiar
                </button>
              )}
            </div>
            <LiveSearch
              value={assigneeQuery}
              onChange={setAssigneeQuery}
              placeholder="Buscar empleado por nombre, email o empresa…"
              testId="assignee-search"
              hotkey={false}
              className="mb-2"
            />
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {directory.length === 0 ? (
                <div className="p-3 text-xs text-slate-500 text-center">Cargando usuarios…</div>
              ) : (() => {
                const visibleUsers = directory.filter((u) =>
                  matchesQuery(assigneeQuery, u.name, u.email, u.empresa, u.departamento)
                );
                if (visibleUsers.length === 0) {
                  return (
                    <div className="p-3 text-xs text-slate-500 text-center">
                      Sin coincidencias para «{assigneeQuery}»
                    </div>
                  );
                }
                return visibleUsers.map((u) => {
                  const selected = form.asignados.some((a) => a.user_id === u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleAssignee(u)}
                      data-testid={`order-assign-${u.id}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-slate-50 ${
                        selected ? "bg-emerald-50/60" : ""
                      }`}
                    >
                      <span className="truncate">
                        {u.name || u.email}
                        {u.departamento && <span className="text-[10px] text-slate-400 ml-1">· {u.departamento}</span>}
                      </span>
                      {selected && <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />}
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          <div>
            <label className="label-up block mb-1">Notas internas</label>
            <textarea className="brutal-input w-full min-h-[50px]" data-testid="order-notas"
              value={form.notas || ""} onChange={(e) => update("notas", e.target.value)} />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold">
            Cancelar
          </button>
          <button type="submit" disabled={saving}
            data-testid="order-form-submit"
            className="flex-1 h-11 rounded-lg bg-[#FF4500] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {isEdit ? "Guardar" : "Crear orden"}
          </button>
        </div>
      </form>

      {pendingPayload && (
        <SerieScopeDialog
          title="Guardar cambios"
          intro="Esta orden forma parte de una serie multi-día. ¿A qué días quieres aplicar los cambios?"
          currentDate={order.fecha}
          members={serieMembers}
          busy={saving}
          confirmLabel="Guardar"
          confirmClass="bg-[#FF4500] text-white border border-[#FF4500]"
          onConfirm={(scope, from, to) => persist(pendingPayload, scope, from, to)}
          onClose={() => setPendingPayload(null)}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Serie-scope dialog — shown when editing/deleting an order that belongs
// to a multi-day serie. Lets the manager pick: only this day / a range /
// the whole serie.
// ─────────────────────────────────────────────────────────────────────────
function SerieScopeDialog({
  title, intro, currentDate, members, busy,
  confirmLabel, confirmClass,
  onConfirm, onClose,
}) {
  const sorted = useMemo(
    () => [...(members || [])].sort((a, b) => (a.fecha || "").localeCompare(b.fecha || "")),
    [members]
  );
  const firstDate = sorted[0]?.fecha || currentDate;
  const lastDate = sorted[sorted.length - 1]?.fecha || currentDate;
  const [scope, setScope] = useState("only"); // "only" | "range" | "series"
  const [rangeFrom, setRangeFrom] = useState(currentDate);
  const [rangeTo, setRangeTo] = useState(currentDate);

  const submit = () => {
    if (scope === "range") {
      if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) {
        toast.error("Rango inválido");
        return;
      }
    }
    onConfirm(scope, rangeFrom, rangeTo);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      data-testid="serie-scope-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-black text-lg text-[#0B2A4A]">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-rose-600">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">{intro}</p>

          <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
            Serie del <b>{firstDate}</b> al <b>{lastDate}</b> · {sorted.length} día(s).
            Día actual: <b>{currentDate}</b>.
          </div>

          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" checked={scope === "only"} onChange={() => setScope("only")}
                data-testid="scope-only" className="mt-1 accent-[#FF4500]" />
              <div>
                <div className="font-semibold text-sm">Sólo este día ({currentDate})</div>
                <div className="text-[11px] text-slate-500">Cambia únicamente la orden del día actual.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" checked={scope === "range"} onChange={() => setScope("range")}
                data-testid="scope-range" className="mt-1 accent-[#FF4500]" />
              <div className="flex-1">
                <div className="font-semibold text-sm">Rango de días</div>
                <div className="text-[11px] text-slate-500 mb-2">Aplica a los días que elijas dentro de la serie.</div>
                {scope === "range" && (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="date" value={rangeFrom} min={firstDate} max={lastDate}
                      onChange={(e) => setRangeFrom(e.target.value)}
                      data-testid="scope-range-from"
                      className="brutal-input h-9 text-sm" />
                    <input type="date" value={rangeTo} min={firstDate} max={lastDate}
                      onChange={(e) => setRangeTo(e.target.value)}
                      data-testid="scope-range-to"
                      className="brutal-input h-9 text-sm" />
                  </div>
                )}
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" checked={scope === "series"} onChange={() => setScope("series")}
                data-testid="scope-series" className="mt-1 accent-[#FF4500]" />
              <div>
                <div className="font-semibold text-sm">Toda la serie ({sorted.length} días)</div>
                <div className="text-[11px] text-slate-500">Aplica a todos los días de la serie.</div>
              </div>
            </label>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold">
            Cancelar
          </button>
          <button type="button" onClick={submit} disabled={busy}
            data-testid="scope-confirm"
            className={`flex-1 h-10 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${confirmClass}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
